import {
  type SubstantiveStory, type FlowNode, type FlowScope, type FlowLane,
  type IdfDetail, type DecideExample, type AExample, LANES,
} from '../pipelineFlow'

// Substantive pipeline view (Ken 2026-09-02). TRUE order on a SLOT × LANE ruler.
// Mini-steps (A/C) carry a TYPE label above + short bold name + inside purpose +
// caption below; their worked-example detail lives in the gutter canvases.
// Decide's confidence RULE now lives inside its worked example (not beside it).

const SCOPE_LABEL: Record<FlowScope, string> = {
  'per-email': 'per email',
  'per-issue': 'per issue',
  'per-issue-x-matter': 'per issue × matter',
  'per-new-matter': 'per new matter',
  'per-existing-matter': 'per existing matter',
}

const ACCENT = { llm: 'var(--me-borderStrong)', code: 'var(--me-fresh)' } as const

const NODE_W = 150
const NODE_H = 84
const STEP = NODE_W + 43
const LANE_H = 132
const INPUT_SPACE = 26
const LEFT = 40
const TOP = 30
const PAD = 28
const MONO = 'var(--me-mono, ui-monospace, monospace)'

const LANE_INDEX = Object.fromEntries(LANES.map((l, i) => [l.id, i])) as Record<FlowLane, number>

export default function SubstantiveFlow({ story, orientation = 'horizontal', compact = false }: { story: SubstantiveStory; orientation?: 'horizontal' | 'vertical' | 'verticalUp'; compact?: boolean }) {
  const nodes = story.nodes
  const x = (slot: number) => slot * STEP
  const nodeY = (lane: FlowLane) => LANE_INDEX[lane] * LANE_H + INPUT_SPACE
  const maxSlot = Math.max(...nodes.map((n) => Math.ceil(n.order ?? 0)))
  const notesY = LANE_INDEX['notes'] * LANE_H + INPUT_SPACE + NODE_H / 2

  const contentW = (maxSlot + 1) * STEP
  const height = (LANES.length - 1) * LANE_H + INPUT_SPACE + NODE_H + 40

  return (
    <div className="me-card mt-3 overflow-auto p-4">
      <div className="mb-3">
        <div className="text-[13px] font-medium me-text">{story.title}</div>
        <div className="text-[11.5px] me-text-mute mt-0.5">{story.question}</div>
        <div className="text-[10.5px] me-text-mute italic mt-1">
          Slot (→) × lane (↓). Outputs below a step, inputs above; a superscript = source slot.
          FILTER / TIE-BREAK mini-steps carry their detail in the worked examples below.
        </div>
      </div>

      {orientation === 'vertical' || orientation === 'verticalUp' ? (
        <VerticalCanvas story={story} up={orientation === 'verticalUp'} compact={compact} />
      ) : (
      <svg width={LEFT + contentW + PAD} height={TOP + height + PAD} className="block">
        <g transform={`translate(${LEFT},${TOP})`}>
          {Array.from({ length: maxSlot + 1 }, (_, s) => (
            <text key={`s${s}`} x={x(s) + NODE_W / 2} y={-14} textAnchor="middle"
              className="fill-[color:var(--me-textMute)] text-[9px]">{s}</text>
          ))}
          {LANES.map((ln, i) => (
            <text key={`l${ln.id}`} x={-14} y={i * LANE_H + INPUT_SPACE + NODE_H / 2} textAnchor="end" dominantBaseline="middle"
              className="fill-[color:var(--me-textMute)] text-[9px]">{i + 1}</text>
          ))}

          {nodes.filter((n) => n.guide).map((n) => (
            <text key={`g${n.key}`} x={x(n.order ?? 0) + NODE_W / 2} y={notesY} textAnchor="middle"
              style={{ fontSize: 17, fontStyle: 'italic', fontFamily: MONO, fill: 'var(--me-text)' }}>{n.guide}</text>
          ))}

          {nodes.map((n) => (
            <Node key={n.key} n={n} x={x(n.order ?? 0)} y={nodeY(n.lane)} />
          ))}
        </g>
      </svg>
      )}

      <div className="mt-3 flex items-center gap-4 text-[10px] me-text-mute">
        <span className="inline-flex items-center gap-1">
          <span style={{ width: 12, height: 10, border: `1.4px solid ${ACCENT.llm}`, borderRadius: 3, display: 'inline-block' }} /> llm step
        </span>
        <span className="inline-flex items-center gap-1">
          <span style={{ width: 12, height: 10, border: `1.4px solid ${ACCENT.code}`, borderRadius: 3, display: 'inline-block' }} /> code step
        </span>
        <span className="inline-flex items-center gap-1">
          <span style={{ width: 12, height: 10, border: `1.4px dashed var(--me-textMute)`, borderRadius: 3, display: 'inline-block' }} /> off-grid dead-end
        </span>
      </div>

      {/* gutter: stacked worked-example canvases — A, A-IDF, then B, then C */}
      {story.aExample && <ACanvas ex={story.aExample} />}
      {story.aIdfDetail && <IdfCanvas detail={story.aIdfDetail} />}
      {story.decideExample && <DecideCanvas ex={story.decideExample} />}
      {story.idfDetail && <IdfCanvas detail={story.idfDetail} />}
    </div>
  )
}

function Node({ n, x, y }: { n: FlowNode; x: number; y: number }) {
  if (n.mini) {
    return (
      <g transform={`translate(${x},${y})`}>
        {n.typeLabel && (
          <text x={0} y={-9} textAnchor="start"
            style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.12em', fill: ACCENT[n.kind] }}>{n.typeLabel}</text>
        )}
        <rect width={NODE_W} height={NODE_H} rx={8} fill="var(--me-surface)" stroke={ACCENT[n.kind]} strokeWidth={1.4} />
        <foreignObject x={0} y={0} width={NODE_W} height={NODE_H}>
          <div style={{ width: '100%', height: '100%', padding: '7px 9px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 3, overflow: 'hidden' }}>
            <div style={{ fontSize: 11, fontWeight: 700, lineHeight: 1.15, color: 'var(--me-text)' }}>{n.name}</div>
            {n.purpose && <div style={{ fontSize: 9, lineHeight: 1.22, color: 'var(--me-textMute)' }}>{n.purpose}</div>}
          </div>
        </foreignObject>
        {n.note && (
          <text x={0} y={NODE_H + 13} textAnchor="start" style={{ fontSize: 8.5, fontFamily: MONO, fill: 'var(--me-textMute)' }}>{n.note}</text>
        )}
      </g>
    )
  }

  const demoted = !!n.demoted
  const ins = n.inputs ?? []
  const outs = n.outputs ?? []
  const right = n.inputAlign === 'right'
  return (
    <g transform={`translate(${x},${y})`}>
      {ins.map((inp, i) => (
        <text key={`in-${inp.v}`} x={right ? NODE_W : 0} y={-8 - (ins.length - 1 - i) * 12} textAnchor={right ? 'end' : 'start'}
          style={{ fontSize: 8.5, fontFamily: MONO, fill: 'var(--me-textMute)' }}>
          {inp.v}
          {inp.sup && <tspan dy={-4} style={{ fontSize: 6.5, fill: 'var(--me-text)' }}>{inp.sup}</tspan>}
        </text>
      ))}

      <rect
        width={NODE_W} height={NODE_H} rx={8}
        fill="var(--me-surface)"
        stroke={demoted || n.manual ? 'var(--me-textMute)' : ACCENT[n.kind]}
        strokeWidth={1.4}
        strokeDasharray={n.manual ? '1.5 3' : demoted ? '4 3' : undefined}
        opacity={demoted ? 0.9 : 1}
      />
      <foreignObject x={0} y={0} width={NODE_W} height={NODE_H}>
        <div style={{ width: '100%', height: '100%', padding: '7px 9px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 3, overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
            {n.scope && <span style={{ fontSize: 8.5, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--me-textMute)' }}>{SCOPE_LABEL[n.scope]}</span>}
            {n.fork && <span className="me-chip" style={{ fontSize: 8 }}>branch</span>}
            {n.manual && <span className="me-chip" style={{ fontSize: 8 }}>human</span>}
          </div>
          <div style={{ fontSize: 11, fontWeight: 500, lineHeight: 1.15, color: demoted ? 'var(--me-textMute)' : 'var(--me-text)' }}>{n.name}</div>
          {n.note && <div style={{ fontSize: 9.5, lineHeight: 1.25, color: 'var(--me-textMute)', overflow: 'hidden' }}>{n.note}</div>}
        </div>
      </foreignObject>

      {outs.map((v, j) => (
        <text key={`out-${v}`} x={NODE_W} y={NODE_H + 12 + j * 12} textAnchor="end"
          style={{ fontSize: 8.5, fontFamily: MONO, fill: 'var(--me-textMute)' }}>{v}</text>
      ))}
    </g>
  )
}

function ACanvas({ ex }: { ex: AExample }) {
  return (
    <div className="mt-4 border-t me-border pt-3">
      <div className="text-[11.5px] font-medium me-text mb-2">{ex.title}</div>
      <div className="flex flex-col gap-1">
        {ex.buckets.map((b, i) => (
          <div key={i} className="text-[9.5px] me-text-mute" style={{ fontFamily: MONO }}>• {b}</div>
        ))}
      </div>
      <div className="text-[10px] me-text font-medium mt-1.5" style={{ fontFamily: MONO }}>{ex.result}</div>
      {ex.note && <div className="text-[9.5px] me-text-mute italic mt-1">{ex.note}</div>}
    </div>
  )
}

function DecideCanvas({ ex }: { ex: DecideExample }) {
  return (
    <div className="mt-4 border-t me-border pt-3">
      <div className="text-[11.5px] font-medium me-text mb-2">{ex.title}</div>
      <div className="flex flex-col gap-0.5 mb-2">
        {ex.rule.map((r, i) => (
          <div key={i} className="text-[9.5px] me-text-mute" style={{ fontFamily: MONO }}>{r}</div>
        ))}
      </div>
      {ex.modifiers && (
        <div className="flex flex-col gap-1 mb-2 pl-2 border-l-2 me-border">
          <div className="text-[9px] uppercase tracking-wider me-text-mute">modifiers — between eligibility &amp; selection</div>
          {ex.modifiers.map((m, i) => (
            <div key={i} className="text-[9.5px] me-text-mute"><span className="me-text font-medium">{m.label}</span> {m.text}</div>
          ))}
        </div>
      )}
      <div className="flex flex-col gap-0.5">
        {ex.rows.map((r, i) => (
          <div key={i} className="flex items-baseline gap-3 text-[9.5px]">
            <span className="me-text" style={{ flex: '0 0 210px' }}>{r.m}</span>
            <span className="me-text-mute" style={{ flex: '0 0 160px', fontFamily: MONO }}>{r.s}</span>
            <span className="me-text-mute">{r.tag}</span>
          </div>
        ))}
      </div>
      <div className="text-[9.5px] me-text-mute mt-1.5" style={{ fontFamily: MONO }}>{ex.idf}</div>
      <div className="text-[9.5px] me-text font-medium mt-0.5" style={{ fontFamily: MONO }}>{ex.verdict}</div>
    </div>
  )
}

function IdfCanvas({ detail }: { detail: IdfDetail }) {
  return (
    <div className="mt-4 border-t me-border pt-3">
      <div className="text-[11.5px] font-medium me-text mb-2">{detail.title}</div>
      <div className="flex items-stretch gap-2 flex-wrap">
        {detail.steps.map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="rounded-md border me-border px-2 py-1.5 text-[9.5px] me-text" style={{ fontFamily: MONO, maxWidth: 260 }}>{s}</div>
            {i < detail.steps.length - 1 && <span className="me-text-mute text-[12px]">→</span>}
          </div>
        ))}
      </div>
      <div className="text-[10.5px] me-text-mute italic mt-2">{detail.tagline}</div>
      {detail.outcomes && (
        <div className="mt-2 flex flex-col gap-1">
          {detail.outcomes.map((o, i) => (
            <div key={i} className="text-[9.5px] me-text-mute">
              <span className="me-text font-medium">{o.cond}</span> → {o.result}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// substantive-2 — vertical layout for portrait. Slots run top→bottom; lanes
// become COLUMNS: existing (left) · main (center) · new (right) · reserved
// (accounting, off-grid to the right). Guides / TYPE labels sit above a step;
// inputs to its left, outputs to its right; the flow reads down the center.
const VLANE_COL: Record<FlowLane, number> = { notes: 1, existing: 0, main: 1, new: 2, reserved: 2 }
const VW = 172, VH = 74, VGAP = 74, VROW = 116, VLEFT = 118, VTOP = 26, VPAD = 24

function VerticalCanvas({ story, up = false, compact = false }: { story: SubstantiveStory; up?: boolean; compact?: boolean }) {
  const nodes = story.nodes
  const maxSlot = Math.max(...nodes.map((n) => Math.ceil(n.order ?? 0)))
  // compact (substantive-4): box shows only the PER label + bold name (no inline
  // description) — wider so long names resolve to ≤2 lines, taller with bottom
  // breathing room, bigger name text. I/O {{vars}} are KEPT.
  const vw = compact ? 228 : VW
  const vgap = compact ? 60 : VGAP
  const boxH = compact ? 60 : VH
  const rowH = compact ? 96 : VROW
  const nameFs = compact ? 12.5 : 10
  const scopeFs = compact ? 9 : 8
  const colX = (lane: FlowLane) => VLANE_COL[lane] * (vw + vgap)
  // up = read bottom→top: slot 0 (Dedup) lands at the bottom.
  const rowY = (slot: number) => (up ? maxSlot - slot : slot) * rowH
  const cols = 3
  const width = VLEFT + cols * vw + (cols - 1) * vgap + VPAD + 60
  const height = VTOP + (maxSlot + 1) * rowH + VPAD

  return (
    <svg width={width} height={height} className="block">
      <g transform={`translate(${VLEFT},${VTOP})`}>
        {Array.from({ length: maxSlot + 1 }, (_, s) => (
          <text key={`vs${s}`} x={-VLEFT + 10} y={rowY(s) + boxH / 2} textAnchor="start" dominantBaseline="middle"
            className="fill-[color:var(--me-textMute)] text-[9px]">{s}</text>
        ))}
        {nodes.map((n) => {
          const cx = colX(n.lane) + (n.demoted ? 34 : 0) // demoted (accounting) sits off-grid
          const cy = rowY(n.order ?? 0)
          const accent = n.demoted || n.manual ? 'var(--me-textMute)' : ACCENT[n.kind]
          const dash = n.manual ? '1.5 3' : n.demoted ? '4 3' : undefined
          const ins = n.inputs ?? []
          const outs = n.outputs ?? []
          return (
            <g key={n.key} transform={`translate(${cx},${cy})`}>
              {n.guide && <text x={vw / 2} y={-6} textAnchor="middle" style={{ fontSize: 13, fontStyle: 'italic', fontFamily: MONO, fill: 'var(--me-text)' }}>{n.guide}</text>}
              {n.typeLabel && <text x={0} y={-6} textAnchor="start" style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.12em', fill: ACCENT[n.kind] }}>{n.typeLabel}</text>}
              {ins.map((inp, i) => (
                <text key={`vi-${inp.v}`} x={-8} y={boxH / 2 - (ins.length - 1) * 6 + i * 12} textAnchor="end"
                  style={{ fontSize: 8, fontFamily: MONO, fill: 'var(--me-textMute)' }}>
                  {inp.v}{inp.sup && <tspan dy={-3} style={{ fontSize: 6, fill: 'var(--me-text)' }}>{inp.sup}</tspan>}
                </text>
              ))}
              <rect width={vw} height={boxH} rx={7} fill="var(--me-surface)" stroke={accent} strokeWidth={1.4} strokeDasharray={dash} opacity={n.demoted ? 0.9 : 1} />
              <foreignObject x={0} y={0} width={vw} height={boxH}>
                <div style={{ width: '100%', height: '100%', padding: compact ? '6px 9px 10px' : '5px 8px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 3, justifyContent: compact ? 'flex-start' : 'center', overflow: 'hidden' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                    {n.scope && <span style={{ fontSize: scopeFs, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--me-textMute)' }}>{SCOPE_LABEL[n.scope]}</span>}
                    {n.fork && <span className="me-chip" style={{ fontSize: 7.5 }}>branch</span>}
                    {n.manual && <span className="me-chip" style={{ fontSize: 7.5 }}>human</span>}
                  </div>
                  <div style={{ fontSize: nameFs, fontWeight: n.mini ? 700 : 600, lineHeight: 1.16, color: n.demoted ? 'var(--me-textMute)' : 'var(--me-text)' }}>{n.name}</div>
                  {!compact && (n.note || n.purpose) && <div style={{ fontSize: 8, lineHeight: 1.2, color: 'var(--me-textMute)', overflow: 'hidden' }}>{n.purpose || n.note}</div>}
                </div>
              </foreignObject>
              {outs.map((v, j) => (
                <text key={`vo-${v}`} x={vw + 8} y={boxH / 2 - (outs.length - 1) * 6 + j * 12} textAnchor="start"
                  style={{ fontSize: 8, fontFamily: MONO, fill: 'var(--me-textMute)' }}>{v}</text>
              ))}
            </g>
          )
        })}
      </g>
    </svg>
  )
}
